import { createTheme, ThemeOptions, responsiveFontSizes } from '@mui/material/styles';
import { PaletteMode } from '@mui/material';
import { grey } from '@mui/material/colors';
import 'typeface-inter';
import {
    spaceGroteskFont,
    powerGroteskFont,
    powerGroteskLightFont,
    headingTypography,
    monospaceFont,
} from './typography';

// Oppy Brand Colors
const brandColors = {
    primary: '#000000', // Black
    secondary: '#4949C8', // Blue
    success: '#21D19F', // Teal
    error: '#ff3b3b', // Red
    accent: '#ceff1a', // Yellow
    info: '#0288D1',
};

// Social Media Colors
const socialColors = {
    twitter: '#1d9bf0',
    facebook: '#1877f2',
    instagram: '#E4405F',
    linkedin: '#0077b5',
};

// Status Colors
const activeStatuses = [
    'active', 
    'pending_assistant_reply', 
    'loading', 
    'loading_messages', 
    'processing_autocomplete',
    'pending_user_reply'
];

const statusColors = {
    closed: brandColors.success,
    error: '#C62828',
    pending_assistant_reply: 'linear-gradient(135deg, #fcc200 0%, #f7971e 100%)',
    pending_user_reply: '#0288D1',
    processing_autocomplete: '#FFC107',
    standby: '#9E9E9E',
    needs_attention: '#F44336',
    loading: '#BBB5FF',
    loading_messages: '#BBB5FF',
    active: '#4CAF50',
    archived: '#795548',
    draft: '#607D8B',
    debugging: '#FF6B6B',
    debugging_pending_assistant_reply: '#FF8A65',
};

// Status Gradients - beautiful gradients for each status
const activeStatusGradient = 'linear-gradient(45deg, transparent 0%, #4949c8 100%)';
const statusGradients = {
    active: activeStatusGradient, // Purple to violet
    pending_user_reply: activeStatusGradient,
    pending_assistant_reply: activeStatusGradient,
    loading: activeStatusGradient,
    loading_messages: activeStatusGradient,
    processing_autocomplete: activeStatusGradient,
    closed: 'linear-gradient(135deg, transparent 0%, #00c853 100%)',
    error: 'linear-gradient(135deg, transparent 0%, #d32f2f 100%)',
    standby: 'linear-gradient(135deg, transparent 0%, rgba(139,145,167,0.7) 100%)',
    needs_attention: 'linear-gradient(135deg, #f85032 0%, #e73827 100%)', // Red gradient
    debugging: 'linear-gradient(135deg, #ff6b6b 0%, #feca57 100%)', // Coral to yellow
    debugging_pending_assistant_reply: 'linear-gradient(135deg, #ff8a65 0%, #ffab91 100%)', // Orange gradient
    draft: 'linear-gradient(135deg, transparent 0%, #bbb5ff 100%)',
    archived: 'linear-gradient(135deg, transparent 0%, #424242 100%)'
};

// Status Animations
const statusAnimations = {
    closed: {},
    error: {
        animation: 'pulsate 1.5s infinite',
        '@keyframes pulsate': {
            '0%': { opacity: 0.5 },
            '50%': { opacity: 1 },
            '100%': { opacity: 0.5 }
        }
    },
};

// Gradient borders for buttons and UI elements
const gradientBorders = {
    primary: (mode: PaletteMode) => mode === 'light'
        ? 'linear-gradient(135deg, rgba(73, 73, 200, 0.6) 0%, rgba(187, 181, 255, 0.8) 50%, rgba(33, 209, 159, 0.6) 100%)'
        : 'linear-gradient(135deg, rgba(73, 73, 200, 0.8) 0%, rgba(187, 181, 255, 0.6) 50%, rgba(33, 209, 159, 0.8) 100%)',
    subtle: (mode: PaletteMode) => mode === 'light'
        ? 'linear-gradient(135deg, rgba(73, 73, 200, 0.3) 0%, rgba(187, 181, 255, 0.4) 50%, rgba(33, 209, 159, 0.3) 100%)'
        : 'linear-gradient(135deg, rgba(73, 73, 200, 0.4) 0%, rgba(187, 181, 255, 0.3) 50%, rgba(33, 209, 159, 0.4) 100%)',
};

// Define custom colors and values that will be consistent across modes
const themeConstants = {
    borderRadius: 8,
    spacing: 8,
    typography: {
        fontFamily: spaceGroteskFont,
        fontFamilyMonospace: monospaceFont,
        ...headingTypography,
        body1: {
            fontFamily: spaceGroteskFont,
            lineHeight: 'normal',
        },
        body2: {
            fontFamily: spaceGroteskFont,
            lineHeight: 'normal',
        },
        subtitle1: {
            fontFamily: spaceGroteskFont,
            lineHeight: 'normal',
        },
        subtitle2: {
            fontFamily: spaceGroteskFont,
            lineHeight: 'normal',
        },
        button: {
            fontFamily: spaceGroteskFont,
            lineHeight: 'normal',
            textTransform: 'none' as const,
        },
        caption: {
            fontFamily: powerGroteskLightFont,
        },
        overline: {
            fontFamily: powerGroteskLightFont,
        },
    },
};

// Define colors for both light and dark modes
const getDesignTokens = (mode: PaletteMode): ThemeOptions => ({
    palette: {
        mode,
        common: {
            black: '#000000',
            white: '#ffffff',
        },
        primary: {
            main: mode === 'light' ? brandColors.primary : '#ffffff',
            contrastText: mode === 'light' ? '#ffffff' : '#000000',
        },
        secondary: {
            main: brandColors.secondary,
            contrastText: '#ffffff',
        },
        success: {
            main: brandColors.success,
            contrastText: '#ffffff',
        },
        error: {
            main: brandColors.error,
            contrastText: '#ffffff',
        },
        info: {
            main: brandColors.info,
            contrastText: '#ffffff',
        },
        // background: {
        //     default: mode === 'light' ? 'rgba(249, 249, 255, 0.9)' : 'rgba(18, 18, 18, 0.9)',
        //     paper: mode === 'light' ? '#f5f5f5' : '#1e1e1e',
        //     // Custom background colors for consistent UI
        //     glass: mode === 'light' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(18, 18, 18, 0.7)',
        //     glassLight: mode === 'light' ? 'rgba(255, 255, 255, 0.4)' : 'rgba(18, 18, 18, 0.4)',
        //     glassStrong: mode === 'light' ? 'rgba(255, 255, 255, 0.85)' : 'rgba(18, 18, 18, 0.85)',
        //     glassHeavy: mode === 'light' ? 'rgba(255, 255, 255, 0.95)' : 'rgba(18, 18, 18, 0.95)',
        //     overlay: mode === 'light' ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.05)',
        //     overlayLight: mode === 'light' ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)',
        //     overlayMedium: mode === 'light' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.2)',
        //     overlayStrong: mode === 'light' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(255, 255, 255, 0.3)',
        //     backdrop: mode === 'light' ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.8)',
        // },
        background: {
            default: mode === 'light' ? 'rgba(249, 249, 255, 0.9)' : 'rgba(24, 24, 28, 0.9)',
            paper: mode === 'light' ? 'rgba(249, 249, 255, 0.98)' : 'rgba(28, 28, 32, 0.98)',
            
            // Glass backgrounds with better blur compatibility
            glass: mode === 'light' 
                ? 'rgba(249, 249, 255, 0.6)' 
                : 'rgba(255, 255, 255, 0.06)',
            glassLight: mode === 'light' 
                ? 'rgba(249, 249, 255, 0.3)' 
                : 'rgba(255, 255, 255, 0.03)',
            // glassMedium: mode === 'light' 
            //     ? 'rgba(249, 249, 255, 0.7)' 
            //     : 'rgba(255, 255, 255, 0.08)',
            glassStrong: mode === 'light' 
                ? 'rgba(249, 249, 255, 0.85)' 
                : 'rgba(255, 255, 255, 0.1)',
            glassHeavy: mode === 'light' 
                ? 'rgba(249, 249, 255, 0.95)' 
                : 'rgba(28, 28, 32, 0.95)',
            
            // Tinted glass options for variety
            
            
            // Overlays
            overlay: mode === 'light' 
                ? 'rgba(73, 73, 200, 0.04)'    // Slight blue tint
                : 'rgba(255, 255, 255, 0.02)',
            overlayLight: mode === 'light' 
                ? 'rgba(73, 73, 200, 0.08)' 
                : 'rgba(255, 255, 255, 0.05)',
            overlayMedium: mode === 'light' 
                ? 'rgba(73, 73, 200, 0.15)' 
                : 'rgba(255, 255, 255, 0.1)',
            overlayStrong: mode === 'light' 
                ? 'rgba(73, 73, 200, 0.25)' 
                : 'rgba(255, 255, 255, 0.15)',
            
            
            // Backdrops for modals/dialogs
            backdrop: mode === 'light' 
                ? 'rgba(0, 0, 0, 0.4)' 
                : 'rgba(0, 0, 0, 0.7)',
            backdropBlur: mode === 'light' 
                ? 'rgba(249, 249, 255, 0.8)' 
                : 'rgba(18, 18, 18, 0.8)',
            
            // Gradient options
            gradient: mode === 'light'
                ? 'linear-gradient(135deg, rgba(249, 249, 255, 0.9) 0%, rgba(187, 181, 255, 0.3) 100%)'
                : 'linear-gradient(135deg, rgba(24, 24, 28, 0.9) 0%, rgba(83, 0, 106, 0.3) 100%)',
            gradientSubtle: mode === 'light'
                ? 'linear-gradient(180deg, rgba(249, 249, 255, 0.5) 0%, transparent 100%)'
                : 'linear-gradient(180deg, rgba(255, 255, 255, 0.05) 0%, transparent 100%)',
        },
        text: {
            primary: mode === 'light' ? '#000000de' : '#ffffff',
            secondary: mode === 'light' ? '#00000099' : '#ffffff99',
        },
        action: {
            active: mode === 'light' ? 'rgba(0, 0, 0, 0.54)' : 'rgba(255, 255, 255, 0.7)',
            hover: mode === 'light' ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.08)',
            selected: mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.16)',
            disabled: mode === 'light' ? 'rgba(0, 0, 0, 0.26)' : 'rgba(255, 255, 255, 0.3)',
            disabledBackground: mode === 'light' ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.12)',
        },
        message: {
            self: mode === 'light' ? grey[300] : grey[700],
            other: mode === 'light' ? grey[200] : grey[800],
        },
    },
    ...themeConstants,
    components: {
        MuiCssBaseline: {
            styleOverrides: (theme) => ({
                '*': {
                    scrollbarWidth: 'none !important',
                    msOverflowStyle: 'none !important',
                    '&::-webkit-scrollbar': {
                        display: 'none !important',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        display: 'none !important',
                    },
                    '&::-webkit-scrollbar-track': {
                        display: 'none !important',
                    },
                },
                body: {
                fontFamily: spaceGroteskFont,
                backgroundColor: mode === 'light' ? 'rgb(249, 249, 255)' : 'rgb(18, 18, 18)',
                backgroundImage: mode === 'dark' 
                    ? 'none'
                    : 'linear-gradient(45deg, rgba(187, 181, 255, 0.12) 0%, rgba(73, 73, 200, 0.1) 14%, rgba(139, 145, 167, 0.08) 28%, rgba(187, 181, 255, 0.1) 22%, rgba(73, 73, 200, 0.12) 46%, rgba(139, 145, 167, 0.09) 60%, rgba(187, 181, 255, 0.11) 74%)',
                backgroundSize: mode === 'dark' ? 'auto' : '5400px 5400px',
                backgroundPosition: 'center center',
                backgroundRepeat: 'no-repeat',
                backgroundAttachment: 'fixed',
                minHeight: '100vh',
                overflow: 'hidden auto',
                },
            }),
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: themeConstants.borderRadius,
                    textTransform: 'none',
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    borderRadius: themeConstants.borderRadius,
                },
            },
        },
        MuiMenu: {
            styleOverrides: {
                paper: {
                    borderRadius: themeConstants.borderRadius,
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    borderRadius: themeConstants.borderRadius,
                },
            },
        },
        MuiListItemText: {
            styleOverrides: {
                primary: {
                    fontFamily: powerGroteskFont,
                    fontWeight: 600,
                    lineHeight: 'normal',
                    letterSpacing: 0,
                }
            }
        },
        MuiListItemIcon: {
            styleOverrides: {
                root: {
                    marginRight: '-20px',
                }
            }
        },
    },
});

export const getTheme = (mode: PaletteMode) => {
    let theme = createTheme(getDesignTokens(mode));
    theme = responsiveFontSizes(theme);
    return {
        ...theme,
        statusColors,
        statusGradients,
        socialColors,
        statusAnimations,
        gradientBorders,
    };
};
